/** @format */

/**
 * 文本匹配筛选项：标签 + 包含/不包含 + 多选/输入。
 * Trace / 会话分析侧栏共用，展开时通过 getFieldOptions 拉取候选值。
 */

import React from 'react'
import { _$, Select, Option } from '@king-design/react'
import styled from 'styled-components'
import { MatchMode } from 'src/pages/AIObservability/SessionAnalysis/typescript'
import { FilterMtchProps } from '../../TraceAnalysis/typescript'
import { MonitorSearch } from 'src/components'
import { useAsyncFn } from 'react-use'
import { getFieldOptions } from 'src/api/ApiAIObservability'

const TEXT_MATCH_OPTS: ReadonlyArray<{ value: MatchMode; label: string }> = [
  { value: 'in', label: _$('包含') },
  { value: 'not_in', label: _$('不包含') }
]
/** OpenTelemetry SpanKind：名称 → kind 值 */
export const SPAN_KIND_ENUM = {
  UNSPECIFIED: 0,
  INTERNAL: 1,
  SERVER: 2,
  CLIENT: 3,
  PRODUCER: 4,
  CONSUMER: 5
} as const

export type SpanKindEnumKey = keyof typeof SPAN_KIND_ENUM
export type SpanKindEnumValue = typeof SPAN_KIND_ENUM[SpanKindEnumKey]

/** kind 值 → 名称（展示/反查） */
export const SPAN_KIND_BY_VALUE: Record<SpanKindEnumValue, SpanKindEnumKey> = {
  0: 'UNSPECIFIED',
  1: 'INTERNAL',
  2: 'SERVER',
  3: 'CLIENT',
  4: 'PRODUCER',
  5: 'CONSUMER'
}

export interface FilterItemProps {
  className?: string
  fieldName: string
  label: string
  op: MatchMode
  values: string[]
  filters: FilterMtchProps[]
  startTime: number
  endTime: number
  isInput?: boolean
  disabled?: boolean
  onChange: (key: string, op: MatchMode, values: string[]) => void
}

const FilterItem: React.FC<FilterItemProps> = ({
  className,
  fieldName,
  label,
  op,
  values,
  filters,
  startTime,
  endTime,
  disabled = false,
  isInput = false,
  onChange
}) => {
  const [{ value: options, loading }, fetchOptions] = useAsyncFn(async () => {
    const result = await getFieldOptions({
      StartTime: startTime,
      EndTime: endTime,
      FieldName: fieldName,
      Filters: filters.filter(item => item.Key !== fieldName)
    })
    if (fieldName === 'kind') {
      return result.map(item => SPAN_KIND_BY_VALUE[item as SpanKindEnumValue])
    }
    return result
  }, [fieldName, filters, startTime, endTime])

  return (
    <div className={className}>
      <span className="trace_filter_label">{_$(label)}：</span>
      <Select
        width={80}
        disabled={disabled}
        className="trace_filter_op"
        value={op ?? 'in'}
        onChangeValue={v => onChange(fieldName, v as MatchMode, values ?? [])}
      >
        {TEXT_MATCH_OPTS.map(o => (
          <Option key={o.value} value={o.value}>
            {_$(o.label)}
          </Option>
        ))}
      </Select>
      {isInput ? (
        <MonitorSearch
          disabled={disabled}
          className="trace_filter_value"
          placeholder={`${_$('请输入')}${label}`}
          value={values?.join(',') ?? ''}
          onChange={v => {
            if (v) {
              onChange(fieldName, op ?? 'in', v.split(','))
            } else {
              onChange(fieldName, op ?? 'in', [])
            }
          }}
        />
      ) : (
        <Select
          fluid
          multiple
          filterable
          clearable
          nowrap
          onShow={() => {
            fetchOptions()
          }}
          loading={loading}
          placeholder={`${_$('请选择')} ${_$(label)}`}
          disabled={disabled}
          className="trace_filter_value"
          value={values}
          onChangeValue={vals => onChange(fieldName, op ?? 'in', vals as string[])}
        >
          {options?.map(item => {
            return (
              <Option key={item} value={item}>
                {item}
              </Option>
            )
          })}
        </Select>
      )}
    </div>
  )
}

export default styled(FilterItem)`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;

  .trace_filter_label {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    font-size: 12px;
    box-sizing: border-box;
    white-space: nowrap;
  }

  .trace_filter_op.k-select {
    flex-shrink: 0;
    width: 90px !important;
    border-radius: 4px 0 0 4px;
    border-right: 0;
  }

  .trace_filter_value {
    flex: 1;
    min-width: 0;

    &.k-select {
      border-radius: 0 4px 4px 0;
    }

    .k-input-wrapper {
      border-radius: 0 !important;
    }
  }
`
